// R2-007 (Simon's second run, decision D9): the project switcher keeps you on the same page.
// A section or tab page (no record id in its address) opens as the same page in the other
// project; a record page (an activity, a ToR, a list) falls back to its section's list page
// (Activities: All activities, not the Overview); anything else, and an address that cannot be
// read (a broken %-escape), opens the other project's Home. The query string is left behind.
// Every address comes from the shared page list (packages/shared/src/pages.ts).
import { PAGES, pageLink, type PageName } from "@convenor/shared";

/** Address parts that are not a record: a calendar month is the same month in any project. */
const KEEP = new Set(["projectId", "month"]);

/** A section whose first page is an overview: its records fall back to this list instead (simon-run2 D6). */
const LIST_OF: Partial<Record<PageName, PageName>> = { activities: "activitiesAll" };

type Pattern = { name: PageName; parts: string[] };

const HOME_PARTS = PAGES.projectHome.split("/");

/** The project pages, as their address parts ("", "p", ":projectId", "activities", ":id"). */
const PROJECT_PAGES: Pattern[] = (Object.keys(PAGES) as PageName[])
  .map((name) => ({ name, parts: PAGES[name].split("/") }))
  .filter((p) => HOME_PARTS.every((part, i) => p.parts[i] === part));

const isParam = (part: string) => part.startsWith(":");

/** The parts a pattern takes from the address ({ projectId, id }), or null when it does not match (or a part cannot be decoded). */
function match(parts: string[], path: string[]): Record<string, string> | null {
  if (parts.length !== path.length) return null;
  const values: Record<string, string> = {};
  for (let i = 0; i < parts.length; i++) {
    if (isParam(parts[i])) {
      try {
        values[parts[i].slice(1)] = decodeURIComponent(path[i]);
      } catch {
        return null;
      }
    } else if (parts[i] !== path[i]) return null;
  }
  return values;
}

const paramCount = (p: Pattern) => p.parts.filter(isParam).length;

/** Build the address of a page from its pattern and values (the same as pageLink, for a name picked at run time). */
const link = (name: PageName, values: Record<string, string>) => (pageLink as (n: PageName, v: Record<string, string>) => string)(name, values);

/** The address to open in project `toProjectId` when the switcher is used on `pathname`. */
export function samePageIn(pathname: string, toProjectId: string): string {
  const home = pageLink("projectHome", { projectId: toProjectId });
  const path = pathname.replace(/\/+$/, "").split("/");
  // The most specific page wins: "activities/new" over "activities/:id".
  const found = PROJECT_PAGES.filter((p) => match(p.parts, path) !== null).sort((a, b) => paramCount(a) - paramCount(b))[0];
  if (!found) return home;
  const values = { ...match(found.parts, path)!, projectId: toProjectId };
  const params = found.parts.filter(isParam).map((p) => p.slice(1));
  if (params.every((p) => KEEP.has(p))) return link(found.name, values);
  // A record page: cut the address before its first record id, then step up until a page has that address;
  // a section that opens on an overview gives its list instead.
  let parts = found.parts.slice(0, found.parts.findIndex((p, i) => i >= HOME_PARTS.length && isParam(p) && !KEEP.has(p.slice(1))));
  while (parts.length > HOME_PARTS.length) {
    const want = parts.join("/");
    const list = PROJECT_PAGES.find((p) => p.parts.join("/") === want);
    if (list && list.parts.filter(isParam).every((p) => p === ":projectId")) return link(LIST_OF[list.name] ?? list.name, values);
    parts = parts.slice(0, -1);
  }
  return home;
}
